import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ChevronLeft, ChevronRight } from 'lucide-angular';
import { ButtonComponent } from '../button/button.component';

/**
 * Reusable pagination control for table/list views.
 */
@Component({
  selector: 'app-pagination',
  imports: [ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css',
})
export class PaginationComponent {
  /** One-based page number currently selected. */
  readonly currentPage = input.required<number>();
  /** Total number of records across all pages. */
  readonly totalCount  = input.required<number>();
  /** Number of records displayed per page. */
  readonly pageSize    = input.required<number>();

  /** Emits the next requested page number. */
  readonly pageChange = output<number>();

  /** Previous-page icon. */
  readonly ChevronLeftIcon  = ChevronLeft;
  /** Next-page icon. */
  readonly ChevronRightIcon = ChevronRight;

  /** Total page count, never lower than one. */
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize())));

  /** One-based index of the first item visible on the current page. */
  readonly startIndex = computed(() => this.totalCount() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1);

  /** One-based index of the last item visible on the current page. */
  readonly endIndex = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalCount()));

  /**
   * Requests the previous page when the current page is not the first page.
   */
  prev(): void {
    if (this.currentPage() > 1) {
      this.pageChange.emit(this.currentPage() - 1);
    }
  }

  /**
   * Requests the next page when the current page is not the last page.
   */
  next(): void {
    if (this.currentPage() < this.totalPages()) {
      this.pageChange.emit(this.currentPage() + 1);
    }
  }
}
